import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    PageHeader,
} from '@bemor/web-kit/ui';

import type { AuditEntry } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useMe, who } from '@/lib/staff';

const PATH = '/staff/audit';
const PAGE_KEY = 'audit';

export const SERVICES = [
    'identity',
    'catalogue',
    'checkout',
    'payments',
    'billing',
    'ledger',
    'risk',
    'delivery',
    'reporting',
    'admin',
] as const;

function Diff({ before, after }: { before: unknown; after: unknown }) {
    if (!before && !after)
        return <span className="text-muted-foreground">—</span>;
    return (
        <details className="text-xs">
            <summary className="cursor-pointer">before / after</summary>
            <pre className="bg-muted mt-1 max-w-md overflow-x-auto rounded p-2">
                {JSON.stringify({ before, after }, null, 2)}
            </pre>
        </details>
    );
}

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const auditPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const auditFilters = filterDefs(PATH, {
    entity_id: 'Entity',
    service: 'Service',
    action: 'Action',
    actor_id: 'Who',
    actor_type: 'Actor type',
    merchant_id: 'Merchant',
    organization_id: 'Organization',
    store_id: 'Store',
    on_behalf_of: 'On behalf of',
    range: 'When',
});

/** BM-93: who the actor was (`actor_type`), for the filter. */
export const ACTOR_TYPES = [
    'staff',
    'user',
    'api_key',
    'buyer',
    'system',
] as const;

export function AuditPage() {
    const me = useMe();
    const columns: ListColumnDef<AuditEntry>[] = [
        {
            key: 'occurred_at',
            aliases: ['when'],
            label: 'When',
            kind: 'datetime',
            sortKey: 'occurred_at',
            csv: (e) => e.occurred_at,
            cell: (e) => <DateTime value={e.occurred_at} empty="—" />,
            locked: 'start',
            noTruncate: true,
            // one line: the date is what a person scans the log by
            cellClassName: 'whitespace-nowrap',
        },
        {
            key: 'service',
            label: 'Service',
            sortKey: 'service',
            cell: (e) => e.service,
            csv: (e) => e.service,
            groupable: { key: 'service', value: (e) => e.service },
        },
        {
            key: 'action',
            label: 'Action',
            sortKey: 'action',
            csv: (e) => e.action,
            cell: (e) => <span className="font-mono text-xs">{e.action}</span>,
        },
        {
            key: 'actor_id',
            aliases: ['actor'],
            label: 'Who',
            sortKey: 'actor_id',
            csv: (e) => e.actor_id,
            cell: (e) => (
                <button
                    type="button"
                    className="underline"
                    onClick={() =>
                        e.actor_id && state.setFilter('actor_id', [e.actor_id])
                    }
                    disabled={!e.actor_id}
                >
                    {who(e.actor_id, me)}
                </button>
            ),
        },
        {
            key: 'actor_type',
            label: 'Actor type',
            csv: (e) => e.actor_type,
            cell: (e) => <span className="text-xs">{e.actor_type ?? '—'}</span>,
            groupable: { key: 'actor_type', value: (e) => e.actor_type ?? '—' },
        },
        {
            key: 'on_behalf_of',
            label: 'On behalf of',
            csv: (e) => e.on_behalf_of,
            // BM-93: the staff member behind an impersonated session
            cell: (e) =>
                e.on_behalf_of ? (
                    <button
                        type="button"
                        className="underline"
                        onClick={() =>
                            state.setFilter('on_behalf_of', [
                                e.on_behalf_of as string,
                            ])
                        }
                    >
                        for {who(e.on_behalf_of, me)}
                    </button>
                ) : (
                    <span className="text-muted-foreground">—</span>
                ),
        },
        {
            key: 'entity_id',
            aliases: ['entity'],
            label: 'Entity',
            csv: (e) => e.entity_id,
            cell: (e) => (
                <button
                    type="button"
                    className="font-mono text-xs underline"
                    onClick={() => state.setFilter('entity_id', [e.entity_id])}
                >
                    {e.entity_type} {e.entity_id}
                </button>
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            csv: (e) => e.merchant_id,
            cell: (e) => (
                <span className="font-mono text-xs">
                    {e.merchant_id ?? '—'}
                </span>
            ),
        },
        {
            key: 'organization_id',
            aliases: ['organization'],
            label: 'Organization',
            csv: (e) => e.organization_id,
            cell: (e) => (
                <span className="font-mono text-xs">
                    {e.organization_id ?? '—'}
                </span>
            ),
            defaultHidden: true,
        },
        {
            key: 'store_id',
            aliases: ['store'],
            label: 'Store',
            csv: (e) => e.store_id,
            cell: (e) => (
                <span className="font-mono text-xs">{e.store_id ?? '—'}</span>
            ),
            defaultHidden: true,
        },
        {
            key: 'diff',
            label: 'Change',
            cell: (e) => <Diff before={e.before} after={e.after} />,
        },
    ];
    const { state, query } = useAdminList<AuditEntry>(PAGE_KEY, PATH, {
        columns,
        filters: auditFilters,
    });
    return (
        <div>
            <PageHeader
                title="Audit log"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Every staff and money action from every service, in one place. Click a person or an entity to filter.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="audit events"
                state={state}
                query={query}
                rowKey={(e) => e.id}
                controls={{
                    entity_id: { kind: 'text', label: 'Entity id' },
                    service: { options: SERVICES },
                    action: { kind: 'text' },
                    actor_id: { kind: 'text' },
                    actor_type: { options: ACTOR_TYPES },
                    merchant_id: { kind: 'text', label: 'Merchant id' },
                    organization_id: { kind: 'text', label: 'Organization id' },
                    store_id: { kind: 'text', label: 'Store id' },
                    on_behalf_of: { kind: 'text', label: 'Staff id' },
                }}
            />
        </div>
    );
}
